import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GridOperatorsService } from '../services/grid-operators.service';
import * as gridOperatorsActions from './grid-operator.actions';
import * as gridOperatorsSelectors from './grid-operator.selectors';
import * as operatorsSelectros from '../../../store/operators/operators.selectors';
import { filter, map, switchMap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { MessageTranslateService } from '../../../shared/services/message.service';

export class GridOperatorsEffects {
  actions$: Actions = inject(Actions);
  store: Store = inject(Store);
  gridOperatorService = inject(GridOperatorsService);
  messageService = inject(MessageTranslateService);

  changeGridOperatorPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gridOperatorsActions.changePagination),
      map((_) => gridOperatorsActions.changePaginationSuccessful())
    )
  );

  loadGridOperatorList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        gridOperatorsActions.loadGridOperatorsList,
        gridOperatorsActions.changePaginationSuccessful,
        gridOperatorsActions.createGridOperatorSuccess,
        gridOperatorsActions.deleteGridOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(gridOperatorsSelectors.selectCurrentPagination),
        this.store.select(operatorsSelectros.selectCurrentOperator),
      ]),
      filter(
        ([_action, _selectedPagination, currentOperator]) =>
          currentOperator?.id != null
      ),
      switchMap(([_action, selectedPagination, currentOperator]) => {
        return this.gridOperatorService
          .getGridOperators(
            currentOperator!.id!,
            selectedPagination?.pageIndex ?? 0,
            selectedPagination?.pageSize ?? 10
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return gridOperatorsActions.loadGridOperatorsListSuccess({
                  gridOperatorsResponse: res.value!,
                });
              }
              return gridOperatorsActions.loadGridOperatorsListFailure({
                error: res.error!,
              });
            })
          );
      })
    )
  );

  updateGridOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gridOperatorsActions.updateGridOperator),
      switchMap((request) => {
        return this.gridOperatorService
          .updateGridOperator(
            request.gridOperatorId,
            request.createGridOperatorRequest
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return gridOperatorsActions.updateGridOperatorSuccess({
                  gridOperator: res.value!,
                });
              }
              return gridOperatorsActions.updateGridOperatorFailure({
                error: res.error!,
              });
            })
          );
      })
    )
  );

  createGridOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gridOperatorsActions.createGridOperator),
      switchMap((request) => {
        return this.gridOperatorService
          .createGridOperator(request.createGridOperatorRequest)
          .pipe(
            map((res) => {
              if (res.isSuccess)
                return gridOperatorsActions.createGridOperatorSuccess({
                  gridOperator: res.value!,
                });

              return gridOperatorsActions.createGridOperatorFailure({
                error: res.error!,
              });
            })
          );
      })
    )
  );

  deleteGridOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gridOperatorsActions.deleteGridOperator),
      switchMap((request) =>
        this.gridOperatorService.deleteGridOperator(request.operatorId).pipe(
          map((res) => {
            if (res.isSuccess) {
              return gridOperatorsActions.deleteGridOperatorSuccess();
            }

            return gridOperatorsActions.deleteGridOperatorFailure({
              error: res.error!,
            });
          })
        )
      )
    )
  );

  showSuccessNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          gridOperatorsActions.createGridOperatorSuccess,
          gridOperatorsActions.updateGridOperatorSuccess,
          gridOperatorsActions.deleteGridOperatorSuccess
        ),
        map(action => {
          if(action.type == gridOperatorsActions.createGridOperatorSuccess.type)
            this.messageService.successTranslate('gridOperators.notifications.save-success-notification');
          if(action.type == gridOperatorsActions.updateGridOperatorSuccess.type)
            this.messageService.successTranslate('gridOperators.notifications.update-success-notification');
          if(action.type == gridOperatorsActions.deleteGridOperatorSuccess.type)
            this.messageService.successTranslate('gridOperators.notifications.delete-success-notification')
        })
      ),
    {
      dispatch: false,
    }
  );

  showErrorNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          gridOperatorsActions.createGridOperatorFailure,
          gridOperatorsActions.updateGridOperatorFailure,
          gridOperatorsActions.deleteGridOperatorFailure
        ),
        map(action => {
          if(action.type == gridOperatorsActions.createGridOperatorFailure.type)
            this.messageService.errorTranslate('gridOperators.notifications.save-failure-notification');
          if(action.type == gridOperatorsActions.updateGridOperatorFailure.type)
            this.messageService.errorTranslate('gridOperators.notifications.update-failure-notification');
          if(action.type == gridOperatorsActions.deleteGridOperatorFailure.type)
            this.messageService.errorTranslate('gridOperators.notifications.delete-failure-notification')
        })
      ),
    {
      dispatch: false,
    }
  );
}
